import { UserMinus, Users } from 'lucide-react';
import { useState } from 'react';

import { iniciais } from '@/lib/formato';
import { EstadoVazio } from '@/ui/components/estado-vazio';
import { SelectPesquisavel } from '@/ui/components/select-pesquisavel';
import { SecaoFormulario } from '@/ui/patterns/secao-formulario';
import { Button } from '@/ui/primitives/button';
import { Input } from '@/ui/primitives/input';
import { Skeleton } from '@/ui/primitives/skeleton';
import { CHAVES_PERFIS_SISTEMA, type EmpresaDetalhe, PERFIS_SISTEMA } from '@fiscoai/contracts';

import { useAlterarMembros, useMembros } from './api';
import { gravar } from './formulario';

const PERFIS_EMPRESA = CHAVES_PERFIS_SISTEMA.filter(
  (c) => PERFIS_SISTEMA[c].escopo === 'EMPRESA',
).map((c) => ({
  valor: c,
  rotulo: PERFIS_SISTEMA[c].nome,
}));

/** Quem acessa esta empresa e com qual perfil. */
export function AbaMembros({ empresa }: { empresa: EmpresaDetalhe }) {
  const membros = useMembros(empresa.id, true);
  const { adicionar, remover } = useAlterarMembros(empresa.id);
  const [email, setEmail] = useState('');
  const [perfil, setPerfil] = useState<string>('fiscal');

  const incluir = () =>
    gravar(async () => {
      await adicionar.mutateAsync({ email, chavePerfil: perfil });
      setEmail('');
    }, 'Pessoa incluída');

  return (
    <SecaoFormulario
      titulo="Quem acessa"
      descricao="A pessoa precisa já ter usuário na instalação. O perfil vale só nesta empresa."
      aoEnviar={() => void incluir()}
    >
      <div className="flex flex-wrap items-end gap-2">
        <label className="grid flex-1 gap-1">
          <span className="text-xs text-texto-suave">E-mail</span>
          <Input
            type="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
            }}
            placeholder="pessoa@empresa.com.br"
          />
        </label>
        <label className="grid gap-1">
          <span className="text-xs text-texto-suave">Perfil</span>
          <SelectPesquisavel
            className="w-52"
            aria-label="Perfil"
            rotuloBusca="Buscar perfil"
            opcoes={PERFIS_EMPRESA}
            valor={perfil}
            aoMudar={(chave) => {
              if (chave) setPerfil(chave);
            }}
          />
        </label>
        <Button
          type="submit"
          variante="primario"
          carregando={adicionar.isPending}
          disabled={!email}
        >
          Incluir
        </Button>
      </div>
      {membros.isPending && <Skeleton className="h-24 rounded-cartao" />}
      {membros.data?.length === 0 && (
        <EstadoVazio
          icone={<Users />}
          titulo="Ninguém vinculado"
          texto="Inclua quem trabalha com as notas desta empresa."
          className="py-6"
        />
      )}
      <ul className="divide-y divide-borda rounded-cartao border border-borda empty:hidden">
        {membros.data?.map((m) => (
          <li key={m.usuario.id} className="flex items-center gap-3 px-4 py-2.5">
            <span
              aria-hidden
              className="grid size-8 place-items-center rounded-pilula bg-marca-suave text-xs font-semibold text-marca"
            >
              {iniciais(m.usuario.nome)}
            </span>
            <span className="grid min-w-0 flex-1 leading-tight">
              <span className="truncate font-medium">{m.usuario.nome}</span>
              <span className="truncate text-xs text-texto-fraco">{m.usuario.email}</span>
            </span>
            <span className="text-sm text-texto-suave">{m.perfil.nome}</span>
            <Button
              variante="fantasma"
              tamanho="icone-sm"
              aria-label={`Tirar ${m.usuario.nome}`}
              onClick={() =>
                void gravar(() => remover.mutateAsync(m.usuario.id), 'Acesso retirado')
              }
            >
              <UserMinus aria-hidden />
            </Button>
          </li>
        ))}
      </ul>
    </SecaoFormulario>
  );
}
